import { ReduxState } from 'app/redux/types';

import { hasAccessToStakesBookmaker, selectAccountBalance } from 'app/lib/account/selectors';
import { StakeType } from 'app/lib/staking/constants';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import { ActionButtonState, AmountValidationResult, StakeErrorCode, SubmissionState } from '../constants';
import {
  selectAnyHasLockedState,
  selectEveryHasLockedState,
  selectHasPriceChanges,
  selectSomeHasError,
} from '../selectors/entries';
import { selectAmountStakeType, selectCurrentStakeType } from '../selectors/stakeTypes';
import { selectExpressAmountValidation, selectExpressFreeBet } from '../selectors/stakeTypes/express';
import {
  selectSingleAmountsValidation,
  selectSingleBetsInfo,
  selectSinglesTotalAmount,
} from '../selectors/stakeTypes/single';
import { selectValidateSystemAmount } from '../selectors/stakeTypes/system';
import { selectConflictState } from '../selectors/state';
import { selectSubmission } from '../selectors/submission';

// ---

function actionButtonState(state: ReduxState): ActionButtonState {
  if (selectSubmission(state) !== SubmissionState.AWAITING) {
    return ActionButtonState.DISABLED;
  }

  if (!selectIsLoggedIn(state)) {
    return ActionButtonState.UNAUTHORIZED;
  }

  if (!hasAccessToStakesBookmaker(state)) {
    return ActionButtonState.ACCESS_IS_RESTRICTED;
  }

  if (selectConflictState(state) !== null) {
    return ActionButtonState.DISABLED;
  }

  const stakeType = selectCurrentStakeType(state);

  if (stakeType.has(StakeType.SINGLE)) {
    const validationResult = selectSingleAmountsValidation(state);

    switch (validationResult) {
      case AmountValidationResult.NO_ERRORS:
      case AmountValidationResult.BIG_STAKE:
        break; // continue evaluation

      case AmountValidationResult.EMPTY_VALUE:
      case AmountValidationResult.MIN_NOT_REACHED:
      case AmountValidationResult.MAX_EXCEEDED:
        return ActionButtonState.DISABLED;

      case AmountValidationResult.UNKNOWN:
      default:
        return ActionButtonState.UNKNOWN;
    }
  }

  if (stakeType.has(StakeType.EXPRESS)) {
    const validationResult = selectExpressAmountValidation(state);

    switch (validationResult) {
      case AmountValidationResult.NO_ERRORS:
      case AmountValidationResult.BIG_STAKE:
        break; // continue evaluation

      case AmountValidationResult.EMPTY_VALUE:
      case AmountValidationResult.MIN_NOT_REACHED:
        return ActionButtonState.DISABLED;

      case AmountValidationResult.UNKNOWN:
      default:
        return ActionButtonState.UNKNOWN;
    }
  }

  if (stakeType.has(StakeType.SYSTEM)) {
    const validationResult = selectValidateSystemAmount(state);

    switch (validationResult) {
      case AmountValidationResult.NO_ERRORS:
      case AmountValidationResult.BIG_STAKE:
        break; // continue evaluation

      case AmountValidationResult.EMPTY_VALUE:
      case AmountValidationResult.MIN_NOT_REACHED:
        return ActionButtonState.DISABLED;

      case AmountValidationResult.UNKNOWN:
      default:
        return ActionButtonState.UNKNOWN;
    }
  }

  const total = totalAmount(state);

  if (insufficientFunds(state, total)) {
    return ActionButtonState.DISABLED;
  }

  if (selectSomeHasError(state) || selectHasPriceChanges(state)) {
    return ActionButtonState.DISABLED;
  }

  const isSingle = stakeType.has(StakeType.SINGLE);

  if (isSingle) {
    if (selectEveryHasLockedState(state)) {
      return ActionButtonState.DISABLED;
    }
  }
  else if (selectAnyHasLockedState(state)) {
    return ActionButtonState.DISABLED;
  }

  return ActionButtonState.READY;
}

export function insufficientFunds(state: ReduxState, total = 0): boolean {
  const freeBet = selectExpressFreeBet(state);

  if (freeBet) {
    return false;
  }

  let balance = selectAccountBalance(state);

  if (!balance || balance < 0) {
    balance = 0;
  }

  const betsInfo = selectSingleBetsInfo(state);

  for (const entry of betsInfo.values()) {
    if (entry.error?.code === StakeErrorCode.NOT_ENOUGH_MONEY) {
      return true;
    }

    if (entry.freeBet || entry.pledgeStake) {
      continue;
    }

    if (!entry.amount) {
      return true;
    }
  }

  if (Number.isNaN(total)) {
    return true;
  }

  return (balance < total);
}

function totalAmount(state: ReduxState): number {
  const stakeType = selectCurrentStakeType(state);

  if (stakeType.has(StakeType.SINGLE)) {
    return selectSinglesTotalAmount(state);
  }

  const amount = selectAmountStakeType(state).numeric;

  if (Number.isNaN(amount)) {
    return NaN;
  }

  if (stakeType.has(StakeType.EXPRESS) || stakeType.has(StakeType.SYSTEM)) {
    return amount;
  }

  return 0;
}

export default actionButtonState;
